
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import { IUser } from "@/models/users";
import {
  IUserProviderData,
  IUserProviderMethods
} from "@/models/providers/user";
import _ from "@/boot/lodash";
import { IProvider } from "@/models/providers";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

@Component({
  computed: {
    ...mapState({
      user(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.USERS,
          scope: this.userScope
        });
      },
      userScope(state, getters) {
        return getters[`data/${DataModules.USERS}/scope`](this.userId);
      }
    })
  }
})
export default class UserProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props

  @Prop({ required: true })
  public readonly userId!: IUser["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    default: () => (vm: UserProvider) => {
      return vm.$router.push({ name: "adminUsers" }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public readonly user!: IUser;
  public readonly userScope!: string;
  public isLoading = true;
  public isReloading = false;
  public isProcessing = {};
  public hasRequestError = false;

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $userData: this.providerData(),
        $userMethods: this.providerMethods
      })
    );
  }

  public created() {
    this.isLoading = true;
    this.loadData();
  }

  public beforeDestroy() {
    // Bin user data
    this.$store.dispatch("data/binSingle", {
      scope: this.userScope,
      storeModule: DataModules.USERS,
      vm: this
    });
  }

  // Provide

  @Provide("userProvider")
  public providerData(): IUserProviderData {
    return {
      user: this.user,
      userId: this.userId,
      identifier: this.userIdentifier,
      isImported: this.isImported,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      isStaged: this.isStaged,
      hasRequestError: this.hasRequestError,
      scope: this.userScope
    };
  }

  @Provide("userMethods")
  public get providerMethods(): IUserProviderMethods {
    return {
      reloadData: this.reloadData
    };
  }

  // Computed

  public get userIdentifier() {
    return _.get(this.user, "fullname") || _.get(this.user, "email");
  }

  public get isStaged() {
    return !!_.get(this.user, "staged_import");
  }

  public get isImported() {
    return !this.isStaged && !!_.get(this.user, "import_id");
  }

  // Methods
  public async loadData({ ignoreStored = false } = {}) {
    return this.getUser(ignoreStored);
  }

  public async getUser(ignoreStored = false): Promise<any> {
    try {
      await this.$store.dispatch(`data/${DataModules.USERS}/get`, {
        id: this.userId,
        scope: this.userScope,
        ignoreStored,
        vm: this,
        params: {
          with: ["userBrandGroup", "brands", "import.credentials.source"].join()
        }
      });
      this.hasRequestError = false;
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      const is404 = this.$store.getters["api/is404"](error);
      if ((is403 || is404) && this.onNotFound) this.onNotFound(this);
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }
}
